import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/auth';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 🔑 Utilise axios (api/auth.js) au lieu de fetch
      const response = await api.post('/login', { email, password });
      const data = response.data;

      // Sauvegarde du token + utilisateur
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      if (rememberMe) {
        localStorage.setItem('remember_email', email);
      }

      // Mise à jour du contexte
      if (login) {
        await login(data.user, data.token);
      }

      // 🔑 1. Si l'utilisateur doit changer son mot de passe
      if (data.user?.must_change_password === true) {
        navigate('/change-password');
        return;
      }

      // 🔑 2. Sinon : redirection selon le rôle
      const role = data.user?.role;

      if (role === 'super_admin') {
        navigate('/super-admin');
      } else if (role === 'admin') {
        navigate('/admin');
      } else if (role === 'teacher' || role === 'principal_teacher') {
        navigate('/teacher');
      } else if (role === 'student') {
        navigate('/student');
      } else if (role === 'parent') {
        navigate('/parent');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Identifiants incorrects'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] overflow-hidden">

      {/* ══════════════════════════════════════════════════════════ */}
      {/* PARTIE GAUCHE — Visuel avec icônes animées                */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0F0F0F] overflow-hidden">

        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#F39C12] to-[#E67E22] opacity-70 z-10"></div>
        <div className="absolute -top-40 right-40 w-[300px] h-[300px] rounded-full border-2 border-[#F39C12] opacity-30 z-10"></div>
        <div className="absolute -bottom-40 -left-20 w-[400px] h-[400px] rounded-full border-2 border-[#F39C12] opacity-20 z-10"></div>
        <div className="absolute -bottom-32 -right-32 w-[350px] h-[350px] rounded-full bg-gradient-to-br from-[#F39C12] to-[#E67E22] opacity-70 z-10"></div>

        <div className="absolute inset-0 z-0">
          <img
            src="/images/livre-student.png"
            alt="SamaEcole"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/50"></div>
        </div>

        <div className="relative z-20 flex flex-col justify-between w-full p-10 lg:p-14">

          <div>
            <img src="/images/logo.png" alt="SamaEcole" className="h-16 w-auto object-contain" />
          </div>

          <div className="max-w-md">
            <h1 className="text-white font-extrabold text-4xl lg:text-5xl leading-[1.1]">
              Apprendre<br />aujourd'hui,
            </h1>
            <p className="text-[#F39C12] italic font-bold text-3xl lg:text-4xl mt-2">
              réussir demain
            </p>
            <div className="w-20 h-0.5 bg-[#F39C12] mt-3"></div>

            <p className="text-gray-200 text-sm leading-relaxed mt-6 max-w-sm">
              SamaEcole accompagne toute votre scolarité avec une plateforme
              simple, moderne et sécurisée.
            </p>
          </div>

          <div className="grid grid-cols-4 gap-4 max-w-lg">
            <LeftFeature icon={<BookIcon className="w-5 h-5 text-[#F39C12]" />} label="Suivi de scolarité" delay="0s" />
            <LeftFeature icon={<GearIcon className="w-5 h-5 text-[#F39C12]" />} label="Gestion simplifiée" delay="0.15s" />
            <LeftFeature icon={<ChatIcon className="w-5 h-5 text-[#F39C12]" />} label="Communication facilitée" delay="0.3s" />
            <LeftFeature icon={<ChartIcon className="w-5 h-5 text-[#F39C12]" />} label="Analyses et rapports" delay="0.45s" />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* PARTIE DROITE — Formulaire                                */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-10 bg-[#0A0A0A] min-h-screen">

        <div className="form-container w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-10 lg:p-14">

          <div className="flex flex-col items-center mb-10">
            <div className="bg-[#0A0A0A] rounded-2xl px-6 py-4 mb-4">
              <img src="/images/logo.png" alt="SamaEcole" className="h-16 w-auto object-contain" />
            </div>
            <p className="text-gray-500 text-sm text-center">
              Votre école, connectée à votre avenir.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900">Se connecter</h2>
            <p className="text-gray-500 text-base mt-2">
              Accédez à votre espace en toute sécurité
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">

            <div className="relative">
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email ou numéro de téléphone"
                required
                className="form-input w-full bg-white border border-gray-300 rounded-xl pl-14 pr-4 py-5 text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:border-[#F39C12] focus:ring-2 focus:ring-[#F39C12]/20"
              />
              <span className="absolute top-1/2 -translate-y-1/2 left-5 text-gray-400">
                <UserIcon className="w-5 h-5" />
              </span>
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                required
                className="form-input w-full bg-white border border-gray-300 rounded-xl pl-14 pr-14 py-5 text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:border-[#F39C12] focus:ring-2 focus:ring-[#F39C12]/20"
              />
              <span className="absolute top-1/2 -translate-y-1/2 left-5 text-gray-400">
                <LockIcon className="w-5 h-5" />
              </span>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 -translate-y-1/2 right-5 text-gray-400 hover:text-[#F39C12] transition"
              >
                <EyeIcon className="w-5 h-5" open={showPassword} />
              </button>
            </div>

            <div className="flex items-center justify-between text-base pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-[#F39C12] focus:ring-[#F39C12] accent-[#F39C12]"
                />
                <span className="text-gray-700">Se souvenir de moi</span>
              </label>
              <Link to="/forgot-password" className="text-[#F39C12] hover:underline font-medium">
                Mot de passe oublié ?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-orange w-full text-white font-semibold py-5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 text-base"
            >
              <LoginArrowIcon className="w-5 h-5" />
              <span>{loading ? 'Connexion...' : 'Se connecter'}</span>
            </button>
          </form>

          <div className="relative my-10">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-gray-400">ou</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => alert('Connexion Google — à implémenter')}
            className="w-full border-2 border-[#F39C12] text-gray-700 font-semibold py-5 rounded-xl flex items-center justify-center gap-3 hover:bg-[#F39C12]/5 transition text-base"
          >
            <GoogleIcon className="w-6 h-6" />
            <span>Continuer avec Google</span>
          </button>

          <div className="mt-10 bg-gray-50 rounded-xl p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#F39C12] flex items-center justify-center flex-shrink-0">
              <ShieldCheckIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base">Votre sécurité est notre priorité</p>
              <p className="text-gray-500 text-sm mt-1">
                Vos données sont protégées et confidentielles.
              </p>
            </div>
          </div>

          
        </div>
      </div>
    </div>
  );
};

function LeftFeature({ icon, label, delay = '0s' }) {
  return (
    <div className="feature-card text-center cursor-pointer group">
      <div
        className="feature-icon w-14 h-14 mx-auto rounded-xl bg-[#F39C12]/15 backdrop-blur-sm flex items-center justify-center mb-3 border border-[#F39C12]/30"
        style={{ animation: `iconFloat 3s ease-in-out infinite`, animationDelay: delay }}
      >
        {icon}
      </div>
      <p className="feature-label text-gray-200 text-[11px] leading-tight font-medium">
        {label}
      </p>
    </div>
  );
}

const UserIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" /></svg>);
const LockIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>);
const EyeIcon = ({ className, open }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">{open ? (<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>) : (<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><path d="M1 1l22 22" /></>)}</svg>);
const LoginArrowIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>);
const GoogleIcon = ({ className }) => (<svg className={className} viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>);
const ShieldCheckIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>);
const BookIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>);
const GearIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>);
const ChatIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>);
const ChartIcon = ({ className }) => (<svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3v18h18" /><path d="M7 12l4 4 8-8" /></svg>);

export default Login;